import React, { useCallback, useMemo } from 'react';
import {
  Alert,
  Button,
  Container,
  Field,
  FormErrorMessage,
  GoodDeal,
  HeaderSection,
  Text,
} from '@groupelacentrale/contact-storybook';
import { IconHelp } from '@groupelacentrale/contact-storybook/lib/icons';
import { CompressedSlider } from 'components/Slider/CompressedSlider';
import { FormikTouched } from 'formik';
import BadgeInformationsModal from './BadgeInformationsModal';
import { useModalState } from 'hooks/use-modal-state';
import './VehiclePriceForm.scss';

interface Props {
  isPro?: boolean;
  showActions?: boolean;
  positions: number[];
  badgesRanges?: {
    min: number;
    badDeal: number;
    equitableDeal: number;
    goodDeal: number;
    veryGoodDeal: number;
    offMarket: number;
    max: number;
  };
  refinedQuotation?: number;
  vehiclePrice: number | undefined;
  isSubmitting: boolean;
  errors: { vehiclePrice?: any };
  errorMessage?: string;
  touched: FormikTouched<{ vehiclePrice?: any }>;
  handleSubmit: () => undefined | void;
  onReturn: () => void | undefined;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

export const VehiclePriceFormView: React.FC<Props> = ({
  isPro = false,
  positions,
  badgesRanges,
  vehiclePrice,
  isSubmitting,
  errors,
  errorMessage,
  touched,
  handleSubmit,
  onReturn,
  setFieldValue,
  showActions,
}) => {
  const { isOpen, close, open } = useModalState(false);

  // Valid pattern [0-9]* : only numbers
  const handlePriceChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value =
        e.currentTarget.value.length > 0 ? Number.parseInt(e.currentTarget.value.replace(/\D/, '')) : undefined;

      if (value === undefined || !isNaN(value)) {
        setFieldValue('vehiclePrice', value);
      }
    },
    [setFieldValue],
  );

  const getGooddealBadgeRate = useMemo(() => {
    if (!badgesRanges || !vehiclePrice) return 0;
    if (vehiclePrice > badgesRanges.offMarket && vehiclePrice <= badgesRanges.veryGoodDeal) return 5;
    else if (vehiclePrice <= badgesRanges.goodDeal && vehiclePrice > badgesRanges.veryGoodDeal) return 4;
    else if (vehiclePrice <= badgesRanges.equitableDeal && vehiclePrice > badgesRanges.goodDeal) return 3;
    else if (vehiclePrice > badgesRanges.equitableDeal && vehiclePrice < badgesRanges.badDeal) return 2;
    else return 1;
  }, [vehiclePrice, badgesRanges]);

  return (
    <Container shadowed paddings="mediumP" backgroundColor="bgWhite">
      <form className="form-badge-selection" onSubmit={handleSubmit}>
        {showActions !== false && <HeaderSection noUnderline>Prix de votre véhicule</HeaderSection>}
        <Field
          type="text"
          pattern="[0-9]*"
          name="vehiclePrice"
          value={vehiclePrice ?? ''}
          onChange={handlePriceChange}
          unit="€*"
          className="price-input"
          disabled={isSubmitting}
        />
        {badgesRanges !== undefined && (vehiclePrice || badgesRanges.goodDeal) && (
          <>
            <CompressedSlider
              ranges={[
                { color: '#D40000', threshold: 0, position: 0 },
                { color: '#00AA0E', threshold: badgesRanges.offMarket, position: positions[0] },
                { color: '#008919', threshold: badgesRanges.veryGoodDeal, position: positions[1] },
                { color: '#005905', threshold: badgesRanges.goodDeal, position: positions[2] },
                { color: '#FF6812', threshold: badgesRanges.equitableDeal, position: positions[3] },
                { color: '#D40000', threshold: badgesRanges.badDeal, position: positions[4] },
                { color: '#000', threshold: badgesRanges.max, position: 100 },
              ]}
              value={vehiclePrice ?? badgesRanges.goodDeal}
              min={badgesRanges.min}
              max={badgesRanges.max}
              onChange={(value: number) => setFieldValue('vehiclePrice', value)}
            />
            <div className="gooddeal-badge">
              <div className="label">Votre positionnement : </div>
              <div className="badge">
                <GoodDeal rate={getGooddealBadgeRate} />
                <IconHelp style={{ cursor: 'pointer' }} width={20} onClick={open} />
              </div>
            </div>
          </>
        )}

        {touched.vehiclePrice && errors.vehiclePrice && (
          <FormErrorMessage name="vehiclePrice" message={errors.vehiclePrice} />
        )}
        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
        <Text size="small">* Champs obligatoires</Text>
        {showActions !== false && (
          <div className="actions">
            <Button label="Retour" theme="quaternary" type="button" onClick={onReturn} />
            <Button
              label={isPro ? 'Déposer' : 'Valider et continuer'}
              type="submit"
              theme="primary"
              disabled={isSubmitting || !vehiclePrice}
              loading={isSubmitting}
            />
          </div>
        )}
      </form>
      <BadgeInformationsModal isOpen={isOpen} onClose={close} />
    </Container>
  );
};
